package com.klantenstop.web.pages.manageCompany.create

import com.materialkobweb.components.toast.ToastManager
import com.klantenstop.web.data.models.domain.Company
import com.klantenstop.web.data.models.dto.requests.address.InsertAddress
import com.klantenstop.web.data.models.dto.requests.address.ValidateAddressDto
import com.klantenstop.web.data.models.dto.requests.company.InsertCompany
import com.klantenstop.web.data.models.dto.requests.company.InsertCompanyStatus
import com.klantenstop.web.data.repos.AddressRepo
import com.klantenstop.web.data.repos.CategoryRepo
import com.klantenstop.web.data.repos.CompanyRepo
import com.klantenstop.web.pages.manageCompany.components.AddressValidationState
import com.materialkobweb.utils.ViewModel
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext

class CreateCompanyViewModel(
    private val companyRepo: CompanyRepo = CompanyRepo,
    private val categoryRepo: CategoryRepo = CategoryRepo,
    private val addressRepo: AddressRepo = AddressRepo
) : ViewModel(), CreateCompanyContract {
    private val _state = MutableStateFlow(CreateCompanyContract.State())
    override val state: StateFlow<CreateCompanyContract.State> = _state.asStateFlow()

    private val _effect = MutableSharedFlow<CreateCompanyContract.Effect>()
    override val effect: SharedFlow<CreateCompanyContract.Effect> = _effect.asSharedFlow()

    init {
        sendIntent(CreateCompanyContract.Intent.LoadInitialData)
    }


    override fun sendIntent(intent: CreateCompanyContract.Intent) = coroutineScope.launch {
        when (intent) {
            is CreateCompanyContract.Intent.LoadInitialData -> loadInitialData()
            is CreateCompanyContract.Intent.Cancel -> emitEffect(CreateCompanyContract.Effect.NavigateBack())
            is CreateCompanyContract.Intent.Save -> saveCompany()
            is CreateCompanyContract.Intent.UpdateName -> updateState { it.copy(name = intent.name) }
            is CreateCompanyContract.Intent.UpdateDescription -> updateState { it.copy(companyDescription = intent.description) }
            is CreateCompanyContract.Intent.UpdateEmailAddress -> updateState { it.copy(emailAddress = intent.emailAddress) }
            is CreateCompanyContract.Intent.UpdatePhoneNumber -> updateState { it.copy(phoneNumber = intent.phoneNumber) }
            is CreateCompanyContract.Intent.UpdateWebsite -> updateState { it.copy(website = intent.website) }
            is CreateCompanyContract.Intent.UpdateCompanyStatus -> updateState { it.copy(selectedStatusType = intent.companyStatus) }
            is CreateCompanyContract.Intent.UpdateCategory -> updateState { it.copy(selectedCategory = intent.companyCategory) }
            is CreateCompanyContract.Intent.UpdateCompanyStatusDescription -> updateState { it.copy(statusDescription = intent.statusDescription) }
            is CreateCompanyContract.Intent.ToggleStatusDescriptionVisibility -> updateState {
                val nowVisible = !it.showStatusDescription
                it.copy(
                    showStatusDescription = nowVisible,
                    statusDescription = if (nowVisible) it.statusDescription else ""
                )
            }
            is CreateCompanyContract.Intent.UpdateStreetName -> updateState { it.copy(streetName = intent.streetName, addressValidationResult = null) }
            is CreateCompanyContract.Intent.UpdateStreetNumber -> updateState { it.copy(streetNumber = intent.streetNumber, addressValidationResult = null) }
            is CreateCompanyContract.Intent.UpdateZipCode -> updateState { it.copy(zipCode = intent.zipCode, addressValidationResult = null) }
            is CreateCompanyContract.Intent.UpdateCity -> updateState { it.copy(city = intent.city, addressValidationResult = null) }
            is CreateCompanyContract.Intent.UpdateCountry -> updateState { it.copy(country = intent.country, addressValidationResult = null) }
            is CreateCompanyContract.Intent.ValidateAddress -> validateAddress()
        }
    }

    private suspend fun loadInitialData() = withContext(Dispatchers.Default) {
        try {
            val categoriesDeferred = async { categoryRepo.getCategories() }

            val categories = categoriesDeferred.await()

            updateState { it.copy(categories = categories) }
        } catch (e: Exception) {
            ToastManager.error(
                e.message ?: "Er is een fout opgetreden bij het laden van de gegevens",
                title = "Laden mislukt"
            )
        }
    }

    private suspend fun saveCompany() {
        val currentState = state.value

        // Mark that save was attempted for inline validation feedback
        updateState { it.copy(hasAttemptedSave = true, callOnRoute = true) }

        // Check if all required fields are valid
        val isFormValid = currentState.isNameValid &&
                currentState.isEmailAddressValid &&
                currentState.isWebsiteValid &&
                currentState.isCategoryValid &&
                currentState.isAddressComplete &&
                currentState.isAddressValidated

        if (!isFormValid) {
            val missingFields = buildList {
                if (!currentState.isNameValid) add("bedrijfsnaam")
                if (!currentState.isEmailAddressValid) add("geldig e-mailadres")
                if (!currentState.isWebsiteValid) add("geldige website URL")
                if (!currentState.isCategoryValid) add("categorie")
                if (!currentState.isAddressComplete) add("volledig adres")
                if (!currentState.isAddressValidated) add("geverifieerd adres")
            }
            
            ToastManager.warning(
                "Controleer de volgende velden: ${missingFields.joinToString(", ")}",
                title = "Ongeldige invoer"
            )
            updateState { it.copy(callOnRoute = false) }
            return
        }

        try {
            val result = createCompany()

            if (result != null) {
                val successMessage = "Bedrijf '${result.name}' succesvol aangemaakt"
                ToastManager.success(successMessage, title = "Gelukt")
                emitEffect(CreateCompanyContract.Effect.NavigateBack(result.publicIdentifier))
            } else {
                ToastManager.error("Er is een fout opgetreden bij het opslaan", title = "Opslaan mislukt")
            }
        } catch (e: Exception) {
            val errorMessage = e.message ?: "Er is een fout opgetreden bij het aanmaken van het bedrijf"
            ToastManager.error(errorMessage, title = "Opslaan mislukt")
        } finally {
            // Reset callOnRoute after handling save
            updateState { it.copy(callOnRoute = false) }
        }
    }

    private suspend fun createCompany(): Company? {
        val currentState = state.value
        val company = InsertCompany(
            name = currentState.name,
            description = currentState.companyDescription,
            address = InsertAddress(
                streetName = currentState.streetName,
                streetNumber = currentState.streetNumber,
                city = currentState.city,
                zipCode = currentState.zipCode,
                country = currentState.country,
                latitude = currentState.addressLatitude,
                longitude = currentState.addressLongitude
            ),
            phoneNumber = currentState.phoneNumber.ifBlank { null },
            emailAddress = currentState.emailAddress.ifBlank { null },
            website = currentState.website.ifBlank { null },
            status = InsertCompanyStatus(
                text = currentState.statusDescription,
                status = currentState.selectedStatusType
            ),
            categories = listOfNotNull(currentState.selectedCategory?.id)
        )

        return companyRepo.createCompany(company)
    }

    private suspend fun validateAddress() {
        val currentState = state.value

        if (!currentState.isAddressComplete) {
            updateState { it.copy(isValidatingAddress = false, addressValidationResult = AddressValidationState.Error) }
            ToastManager.warning(
                "Vul alle adresvelden in voordat u valideert. Kijk ook alle velden na op (spel)fouten.",
                title = "Onvolledig of fout adres"
            )
            return
        }

        updateState { it.copy(isValidatingAddress = true, addressValidationResult = null) }

        try {
            val validationRequest = ValidateAddressDto(
                streetName = currentState.streetName,
                streetNumber = currentState.streetNumber,
                zipCode = currentState.zipCode,
                city = currentState.city,
                country = currentState.country
            )

            val validationResponse = addressRepo.validateAddress(validationRequest)

            if (validationResponse == null) {
                updateState {
                    it.copy(
                        isValidatingAddress = false,
                        addressValidationResult = AddressValidationState.Error
                    )
                }
                return
            }

            if (validationResponse.valid && validationResponse.latitude != null && validationResponse.longitude != null) {
                updateState {
                    it.copy(
                        isValidatingAddress = false,
                        addressValidationResult = AddressValidationState.Success,
                        addressLatitude = validationResponse.latitude,
                        addressLongitude = validationResponse.longitude
                    )
                }
            } else {
                updateState {
                    it.copy(
                        isValidatingAddress = false,
                        addressValidationResult = AddressValidationState.Error
                    )
                }
            }
        } catch (_: Exception) {
            updateState {
                it.copy(
                    isValidatingAddress = false,
                    addressValidationResult = AddressValidationState.Error
                )
            }
        }
    }

    override fun emitEffect(effect: CreateCompanyContract.Effect) = coroutineScope.launch {
        _effect.emit(effect)
    }

    override fun updateState(block: (CreateCompanyContract.State) -> CreateCompanyContract.State) {
        _state.update { currentState -> block(currentState) }
    }
}
